<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <collapsible-container
    :title="title"
    :id="player.pin"
    @updateTitle="updateTitle"
    class="player-card"
    :class="{off:!isOn}">

    <template v-slot:image>
      <div v-if="isBusy"
           class="thumbnail"
           :class="{'no-image':!thumbnailUrl}"
           :style="{backgroundImage:`url(${thumbnailUrl})`}">
      </div>
      <div class="online-indicator" :class="{online:isOn}"></div>
    </template>

    <template v-slot:sub-title>
      <span class="pin">{{player.pin}}</span>
      <span>{{location}}</span>
    </template>

    <template v-slot:content>
      <div class="info" :class="{off:!isOn}">
        <div class="desc">{{deviceDescription}}</div>
        <div class="desc">{{screensDescription}}</div>
      </div>
      <div class="screen-bounds" :style="screenBoundsStyle">
        <div class="screen"
             v-for="(screen, i) in screens"
             :key="screen.displayID"
             :style="screenStyle(screen)">
          <div class="screen-desc">
            <span class="number">{{i + 1}}</span>
          </div>
        </div>
      </div>
      <div class="screens-desc-box">
        <div class="screen-desc desc" v-for="(screen, i) in screens" :key="screen.displayID">
          <span class="number">{{i + 1}}</span>
          <span v-if="screen.isBuiltin">Builtin</span>
          <span class="name">{{screen.displayName}}</span>
          <span class="resolution">
            <span>{{screen.resolution.w}}x{{screen.resolution.h}}</span>
            <span v-if="screen.pixelDensity > 1"> @{{screen.pixelDensity}}x</span>
          </span>
        </div>
      </div>
    </template>

    <template v-slot:content-tools>
      <button class="close">Remove Player</button>
      <button class="close">Settings</button>
    </template>

  </collapsible-container>
</template>

<script>
import { Artwork } from '../../models/ArtworkData'
import { mapState } from 'vuex'
import CollapsibleContainer from './UI/CollapsibleContainer'

/**
 * @typedef {{
 * resolution : {w:number, h:number},
 * pixelDensity : number,
 * screenOrigin : {x:number, y:number},
 * rotation :  number,
 * screenDimensions : {w:number, h:number},
 * isBuiltin : boolean,
 * isStereo : boolean,
 * displayID : number,
 * displayName : string
 * }} DisplayInfo
 */
/**
 * @typedef {{
 *   model : string,
 *   modelDescription : string,
 *   name : string,
 *   processorCores : number,
 *   processorSpeed : number,
 *   processorArchitecture : string,
 *   memorySize : number,
 *   memoryType : string,
 *   graphicsMemorySize : number,
 *   graphicsMemoryType : string,
 *   storageSize : number,
 *   storageType : string,
 *   os : string,
 *   osVersion : string,
 *   displays : DisplayInfo[],
 *   pxPlayerVersion : string
 * }} deviceInfo
 */

export default {
  name: 'player-card',
  props: {
    /** @type Player */
    player: { type: Object, required: true }
  },

  components: { CollapsibleContainer },

  computed: {
    ...mapState(['accountArtworks']),

    title () {
      if (this.player.title) {
        return this.player.title
      }
      return this.player.deviceInfo
        ? this.player.deviceInfo.name
        : 'Untitled Player'
    },

    location () {
      return this.player.location
        ? this.player.location.name
        : 'Unknown location'
    },

    deviceDescription () {
      return this.player.deviceInfo
        ? `${this.player.deviceInfo.modelDescription}`
        : ''
    },

    /**
     * @return {DisplayInfo[]}
     */
    screens () {
      return (this.player.deviceInfo && this.player.deviceInfo.displays)
        ? Object.keys(this.player.deviceInfo.displays).map(k => this.player.deviceInfo.displays[k])
        : []
    },

    screensDescription () {
      if (this.screens.length) {
        return 'Display' + (this.screens.length > 1 ? 's' : '') + ':'
        // const displays = Object.keys(this.screens).map(k => {
        //   const display = this.screens[k]
        //   const scrDim = display.screenDimensions
        //   const diagonalInch = Math.sqrt(scrDim.w * scrDim.w + scrDim.h * scrDim.h) * 0.0393701
        //   return `${Math.round(diagonalInch * 10) / 10}-inch` + (display.isBuiltin ? ' (built-in)' : '')
        // })
        // return `${displays.join('-')}`
      }
      return ''
    },

    isOn () {
      return !!this.player.connections && Object.keys(this.player.connections).length > 0
    },

    isBusy () {
      return !!this.player.artwork
    },

    artworkTitle () {
      if (!this.player.artwork) return ''
      const aw = this.player.artwork
      return `${aw.title} ${this.artworkSetupAbbr}`
    },

    artworkSetupAbbr () {
      const setup = this.artworkSetupsNumber > 1 ? `S${(parseInt(this.player.artwork.setupId) + 1)}` : ''
      const channel = this.artworkSetupChannelsNumber > 1 ? `C${(this.player.artwork.channelId + 1)}` : ''
      const sep = setup && channel ? ':' : ''
      return setup + sep + channel
    },

    artworkSetupsNumber () {
      return this.connectedArtwork ? this.connectedArtwork.setups.length : 0
    },

    artworkSetupChannelsNumber () {
      return this.connectedArtwork
        ? this.connectedArtwork.setups[this.player.artwork.setupId].channels.length
        : 0
    },

    connectedArtwork () {
      if (!this.player.artwork) return null
      return this.artworks.find(w => w.key === this.player.artwork.key)
    },

    thumbnailUrl () {
      return this.connectedArtwork ? this.connectedArtwork.thumbnailUrl() : ''
    },

    artworks () {
      if (this.accountArtworks) {
        return this.accountArtworks.map(it => Artwork.fromJson(it))
      }
      return []
    },

    /**
     * @type {{ x:number, y:number, w:number, h:number }}
     */
    allScreensRect () {
      const bounds = this.screens.reduce((bounds, scr) => {
        const origin = scr.screenOrigin
        if (origin.x < bounds.L) bounds.L = origin.x
        if (origin.y < bounds.T) bounds.T = origin.y
        const R = origin.x + scr.resolution.w
        const B = origin.y + scr.resolution.h
        if (R > bounds.R) bounds.R = R
        if (B > bounds.B) bounds.B = B
        return bounds
      }, { T: 0, R: 0, B: 0, L: 0 })
      return { x: bounds.L, y: bounds.T, w: bounds.R - bounds.L, h: bounds.B - bounds.T }
    },

    screenBoundsStyle () {
      return { paddingTop: this.allScreensRect.h / this.allScreensRect.w * 100 + '%' }
    }
  },

  methods: {
    updateTitle () {},
    /**
     * @param {DisplayInfo} screen
     * @return {Object}
     */
    screenStyle (screen) {
      const bounds = this.allScreensRect
      const w = screen.resolution.w / bounds.w
      const h = screen.resolution.h / bounds.h
      const top = (screen.screenOrigin.y - bounds.y) / bounds.h
      const left = (screen.screenOrigin.x - bounds.x) / bounds.w

      return {
        width: w * 100 + '%',
        height: h * 100 + '%',
        top: top * 100 + '%',
        left: left * 100 + '%'
      }
    }
  }
}
</script>

<style lang="scss">
  @import "../../scss/vars";
  @import "../../scss/mixins";

  .dark {
    .player-card {
      .title.off {
        color: darken($px-color-grey, 20%);
      }

      .online-indicator {
        background: darken($px-color-grey, 8%);
      }
    }
  }

  .player-card {
    background: $bg-color;
    min-height: $basic-size * 1.5;
    //padding: $basic-padding;
    margin-bottom: $basic-padding;

    font-size: 1.2rem;

    .title {
      &.off {
        color: $px-color-grey;
      }
    }

    .sub-title {
      .pin {
        font-weight: 400;
        margin-right: $small-padding;
      }
    }

    .image-box {
      display: flex;
      flex-flow: column;
      align-self: stretch;

      .thumbnail {
        width: $basic-size;
        height: $basic-size;
        flex-shrink: 0;
      }

      .online-indicator {
        flex-shrink: 0;
        flex-grow: 1;
        flex-basis: $basic-size / 2;

        position: relative;
        width: $basic-size;
        background: $px-color-grey;

        &:after {
          content: 'OFF';
          color: darken($px-color-grey, 40%);
          font-size: 8px;
          font-weight: 400;
          @include centered;
        }

        &.online {
          background: $px-color-green;

          &:after {
            content: 'ON';
          }
        }
      }
    }

    .content-box {
      //border-top: 1px solid lighten($px-color-grey, 30%);
      padding: $basic-size/2;
      & > *:not(:last-child) {
        margin-bottom: $basic-padding * 2;
      }
    }

    .screen-bounds {
      position: relative;
      width: 100%;
      height: 0;

      .screen {
        display: flex;
        align-items: center;
        justify-content: center;

        padding: 1px;
        position: absolute;

        &:before {
          content: '';
          display: block;
          position: absolute;
          background: $px-color-grey;
          width: calc(100% - 1px);
          height: calc(100% - 1px);
          top: 0;
          left: 0;
          z-index: 0;
        }

        .screen-desc {
          position: relative;
          text-align: center;
          color: #fff;
          z-index: 2;
        }
      }
    }

    .desc {
      display: flex;
      align-items: center;
      min-height: $basic-size / 2;
    }

    .screen-desc {
      position: relative;
      display: flex;

      & > *:not(:last-child) {
        margin-right: $small-padding;
      }

      .number {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: $basic-size / 2;
        height: $basic-size / 2;
        /*background: #00ff60;*/
        border-radius: 50%;
        border: 1px solid darken($px-color-grey, 10%);
      }
    }
  }
</style>
